// Call graph construction (proposal §7.3, Phase F).
//
// Given an entry symbol, walk the `calls` edges of the graph to build a call
// tree. Two directions are supported:
//
//   forward (default): "what does this function call, transitively?" — follow
//     `calls` edges in their natural direction (caller → callee).
//   reverse: "who calls this function, up to the entry point?" — follow `calls`
//     edges against their direction (callee → caller), the dual used for impact
//     analysis. Entry points surface at the leaves of the tree.
//
// Cycles (recursion, mutual recursion) are detected per root-to-node path and
// truncated rather than expanded, so the tree is always finite. Repeated
// neighbours on the same node are de-duplicated (the first call site's line is
// kept), keeping the tree compact.

import type { DependencyGraph } from "../dependency/types.js";

export interface CallGraphNode {
  /** Callee symbolId (or a bare name when the call was not resolved). */
  symbolId: string;
  /** Display name when the node resolves to a known symbol. */
  symbolName?: string;
  /** Containing file when the node resolves to a known symbol. */
  filePath?: string;
  /** 1-based line of the call site in the *caller* (undefined for the root). */
  line?: number;
  /** BFS depth from the entry symbol (0 for the root). */
  depth: number;
  /** Direct callees, in source order, de-duplicated by symbolId. */
  children: CallGraphNode[];
  /** Set when expansion stopped because the symbol is an ancestor (a cycle). */
  truncated?: boolean;
}

/** Traversal direction: callees (forward) or callers (reverse). */
export type CallGraphDirection = "forward" | "reverse";

export interface CallGraph {
  /** The entry symbolId the graph was rooted at. */
  entry: string;
  /** The maximum depth explored. */
  maxDepth: number;
  /** Traversal direction used to build the graph. */
  direction: CallGraphDirection;
  /** Root node (the entry symbol). */
  root: CallGraphNode;
  /** Total nodes in the tree (including the root and truncated leaves). */
  nodeCount: number;
}

export interface BuildCallGraphOptions {
  graph: DependencyGraph;
  /** Entry symbolId (filePath#name) to root the call graph at. */
  entry: string;
  /** Maximum traversal depth; default 3. */
  maxDepth?: number;
  /**
   * Traversal direction; default "forward".
   * - "forward": follow `calls` edges caller → callee (what does X call?).
   * - "reverse": follow `calls` edges callee → caller (who calls X?).
   */
  direction?: CallGraphDirection;
}

interface CallTarget {
  /** Next node's symbolId (callee for forward, caller for reverse). */
  to: string;
  line?: number;
}

/** Bare name portion of a symbolId (`file#name` → `name`); identity otherwise. */
function symbolName(symbolId: string): string {
  const hash = symbolId.indexOf("#");
  return hash === -1 ? symbolId : symbolId.slice(hash + 1);
}

/**
 * Build a call graph rooted at `entry` by following `calls` edges up to
 * `maxDepth`. Pure — no IO. Unknown entry symbols still yield a single-node tree
 * so callers can render a meaningful (empty) result.
 *
 * In "forward" mode each edge `from → to` is indexed by its caller, so a node's
 * children are its callees. In "reverse" mode it is indexed by its callee, so a
 * node's children are its callers; unresolved edges (whose `to` is a bare name)
 * are matched against the current symbol's name as well as its full id.
 */
export function buildCallGraph(options: BuildCallGraphOptions): CallGraph {
  const { graph, entry, maxDepth = 3, direction = "forward" } = options;

  // Adjacency over `calls` edges. Forward keys by caller (edge.from) and steps
  // to the callee; reverse keys by callee (edge.to) and steps to the caller.
  // For reverse, `byName` additionally indexes resolved targets by their bare
  // name so a bare entry symbol can match `file#name` edges.
  const adjacency = new Map<string, CallTarget[]>();
  const byName = new Map<string, CallTarget[]>();

  const push = (map: Map<string, CallTarget[]>, key: string, target: CallTarget): void => {
    const list = map.get(key);
    if (list) list.push(target);
    else map.set(key, [target]);
  };

  for (const edge of graph.edges) {
    if (edge.kind !== "calls") continue;
    const line = edge.evidence[0]?.line;
    if (direction === "forward") {
      const target: CallTarget = { to: edge.to };
      if (line !== undefined) target.line = line;
      push(adjacency, edge.from, target);
    } else {
      // Reverse: the next node is the caller (edge.from). Index by the callee
      // (edge.to) for exact matches, and by its bare name for partial matches.
      const target: CallTarget = { to: edge.from };
      if (line !== undefined) target.line = line;
      push(adjacency, edge.to, target);
      if (edge.to.includes("#")) push(byName, symbolName(edge.to), target);
    }
  }

  /** Resolve the neighbours (callees or callers) of `symbolId`, de-duplicated. */
  const neighbours = (symbolId: string): CallTarget[] => {
    if (direction === "forward") return adjacency.get(symbolId) ?? [];

    const seen = new Set<string>();
    const out: CallTarget[] = [];
    const collect = (list: CallTarget[] | undefined): void => {
      for (const target of list ?? []) {
        if (seen.has(target.to)) continue;
        seen.add(target.to);
        out.push(target);
      }
    };
    // Exact match: edge.to === symbolId.
    collect(adjacency.get(symbolId));
    const name = symbolName(symbolId);
    if (symbolId.includes("#")) {
      // Fully-qualified entry: also match unresolved edges whose bare `to`
      // equals this symbol's name (no '#', so they live in `adjacency`).
      if (name !== symbolId) collect(adjacency.get(name));
    } else {
      // Bare entry: match resolved `file#name` edges ending with `#name`.
      collect(byName.get(name));
    }
    return out;
  };

  let nodeCount = 0;

  const build = (
    symbolId: string,
    depth: number,
    callLine: number | undefined,
    pathSet: Set<string>,
  ): CallGraphNode => {
    nodeCount += 1;

    const astNode = graph.ast.nodes.get(symbolId);
    const symbolInfo =
      astNode?.kind === "symbol" ? astNode.symbolInfo : undefined;

    const node: CallGraphNode = { symbolId, depth, children: [] };
    if (symbolInfo && astNode) {
      node.symbolName = symbolInfo.name;
      node.filePath = astNode.filePath;
    }
    if (callLine !== undefined) node.line = callLine;

    // Ancestor revisit → cycle; record and stop expanding.
    if (pathSet.has(symbolId)) {
      node.truncated = true;
      return node;
    }
    if (depth >= maxDepth) return node;

    const nextPath = new Set(pathSet);
    nextPath.add(symbolId);

    const seen = new Set<string>();
    for (const { to, line } of neighbours(symbolId)) {
      if (seen.has(to)) continue;
      seen.add(to);
      node.children.push(build(to, depth + 1, line, nextPath));
    }
    return node;
  };

  const root = build(entry, 0, undefined, new Set());
  return { entry, maxDepth, direction, root, nodeCount };
}
